/**
 * Device Detail — TASK-019 handoff implementation
 *
 * Screen: S2 — Device Detail (/devices/:serial)
 * Actor: Office Admin / Customer Support
 * API: device-service via gateway (/api/v1/devices/:serial, /assign, /activate, /suspend, /unsuspend, /decommission, /testlab/unlock)
 * States: loading / error / success / confirming action (4 states per SOTA §3)
 * SOTA §2: only library primitives — AppCard, AppBadge, AppButton, AppModal, AppField, AppInput, AppTable
 */

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AppBadge,
  AppButton,
  AppCard,
  AppField,
  AppInput,
  AppModal,
  AppTable,
  ConfirmAction,
} from "../../../shared/components/ui";
import {
  IconCheck,
  IconChevronRight,
  IconError,
  IconInfo,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { api, ApiError } from "../../../shared/api/client";
import { readEntityPrefill, withEntityQuery } from "../../../shared/navigation/entityPrefill";
import { searchCustomers } from "../../payments/api";
import type { CustomerSearchRow } from "../../payments/types";
import { TokenDeviceWizard } from "../components/TokenDeviceWizard";

// ─── Types ───────────────────────────────────────────────────────────────────

type MonitoringBlock = {
  paid_through: string | null;
  days_remaining: number | null;
  access_state: "owned" | "active" | "expired" | "locked" | "suspended" | "inactive";
  effective_locked: boolean;
  last_token_masked: string | null;
};

type CustomerInfo = {
  id: number;
  customer_number: string;
  name: string;
  phone: string;
};

type DeviceData = {
  id: number;
  serial_number: string;
  is_paygo: boolean;
  model: string | null;
  capacity: string | null;
  status: string;
  is_locked: boolean;
  token_count: number;
  starting_code: number | null;
  value_divider: number;
  restricted_digit_set: boolean;
  secret_key_masked: string | null;
  has_openpaygo_credentials: boolean;
  manufacturer_name: string | null;
  device_type_name: string | null;
  customer: CustomerInfo | null;
  assigned_at: string | null;
  activated_at: string | null;
  unlocked_at: string | null;
  monitoring: MonitoringBlock;
  created_at: string;
};

// ─── Status badge helpers (SOTA §5: icon + label, not colour alone) ───────────

function AccessStateBadge({ state }: { state: MonitoringBlock["access_state"] }) {
  const map: Record<MonitoringBlock["access_state"], { tone: "success" | "warning" | "error" | "info" | "neutral"; label: string; icon: React.ReactNode }> = {
    owned:     { tone: "success", label: "Owned (fully paid)",    icon: <IconCheck size={12} /> },
    active:    { tone: "success", label: "Active",                icon: <IconCheck size={12} /> },
    expired:   { tone: "error",   label: "Expired",               icon: <IconError size={12} /> },
    locked:    { tone: "warning", label: "Locked (awaiting pay)", icon: <IconWarning size={12} /> },
    suspended: { tone: "error",   label: "Suspended",             icon: <IconError size={12} /> },
    inactive:  { tone: "neutral", label: "Inactive / Unassigned", icon: <IconInfo size={12} /> },
  };
  const { tone, label, icon } = map[state] ?? map.inactive;
  return <AppBadge tone={tone}>{icon} {label}</AppBadge>;
}

function DeviceStatusBadge({ status }: { status: string }) {
  const toneMap: Record<string, "success" | "warning" | "error" | "info" | "neutral"> = {
    active:          "success",
    unassigned:      "neutral",
    assigned:        "info",
    suspended:       "error",
    decommissioned:  "error",
    unlocked:        "success",
  };
  return (
    <AppBadge tone={toneMap[status] ?? "neutral"}>
      {status.toUpperCase()}
    </AppBadge>
  );
}

// ─── Assign Customer modal ────────────────────────────────────────────────────

function AssignCustomerModal({
  serial,
  prefillCustomerId,
  open,
  onClose,
  onSuccess,
}: {
  serial: string;
  prefillCustomerId: number | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<CustomerSearchRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [prefillDone, setPrefillDone] = useState(false);

  const search = useQuery({
    queryKey: ["device-assign-customer-search", query.trim()],
    queryFn: () => searchCustomers(query.trim()),
    enabled: open && selected === null && query.trim().length >= 2,
    retry: 1,
    staleTime: 15_000,
  });

  const reset = () => {
    setQuery("");
    setSelected(null);
    setErr("");
    setPrefillDone(false);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  useEffect(() => {
    if (!open || prefillDone || prefillCustomerId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<{
          customer: {
            id: number;
            customer_number: string;
            first_name?: string | null;
            last_name?: string | null;
            phone?: string | null;
            ward?: string | null;
          };
        }>(`/customers/${prefillCustomerId}`);
        if (cancelled || !d.customer) return;
        const c = d.customer;
        const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim() || c.customer_number;
        setSelected({
          id: c.id,
          customer_number: c.customer_number,
          name,
          phone: c.phone ?? "",
          ward: c.ward ?? null,
          active_contract: null,
        });
        setQuery(c.customer_number || String(c.id));
      } catch {
        if (!cancelled) {
          setErr("Could not load the selected customer — search to continue");
        }
      } finally {
        if (!cancelled) setPrefillDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, prefillCustomerId, prefillDone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      setErr("Search and select a customer first");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/devices/${serial}/assign`, { customer_id: selected.id });
      reset();
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Assignment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Assign Device to Customer"
    >
      <form onSubmit={handleSubmit}>
        <AppField label="Find customer" error={err || undefined}>
          {selected ? (
            <div className="pg-option selected" style={{ display: "block", marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                <div>
                  <div className="o-name">{selected.name}</div>
                  <div className="pg-option-meta">
                    {selected.customer_number}
                    {selected.phone ? ` · ${selected.phone}` : ""}
                    {selected.ward ? ` · ${selected.ward}` : ""}
                    {" · "}ID {selected.id}
                  </div>
                </div>
                <button
                  type="button"
                  className="pg-linklike"
                  onClick={() => {
                    setSelected(null);
                    setErr("");
                  }}
                  disabled={busy}
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <>
              <AppInput
                id="assign-customer-query"
                type="search"
                placeholder="Name, customer no. or phone — type 2+ characters"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setErr("");
                }}
                disabled={busy}
                autoFocus
                inputMode="search"
                aria-label="Search customer"
              />
              {query.trim().length >= 2 ? (
                search.isPending ? (
                  <div className="pg-idem-note" style={{ marginTop: 8 }}>
                    <IconSpinner size={14} /> Searching customers…
                  </div>
                ) : search.isError ? (
                  <div className="pg-inline-error" role="alert" style={{ marginTop: 8 }}>
                    <IconError size={14} /> Customer search failed — check your connection and retry.
                  </div>
                ) : search.data && search.data.customers.length === 0 ? (
                  <div className="pg-inline-error" role="alert" style={{ marginTop: 8 }}>
                    <IconWarning size={14} /> No match — check the number, or register the customer first.
                  </div>
                ) : (
                  <div className="pg-option-list" style={{ marginTop: 8 }}>
                    {search.data?.customers.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="pg-option"
                        onClick={() => {
                          setSelected(c);
                          setErr("");
                        }}
                      >
                        <span>
                          <span className="o-name">{c.name}</span>
                          <span className="pg-option-meta">
                            {c.customer_number}
                            {c.phone ? ` · ${c.phone}` : ""}
                            {c.ward ? ` · ${c.ward}` : ""}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )
              ) : (
                <p className="pg-helper-text" style={{ marginTop: 8 }}>
                  Same live search as payment recording — pick a household from the list, then assign.
                </p>
              )}
            </>
          )}
        </AppField>
        <p className="pg-helper-text">
          After assign, status becomes <strong>ASSIGNED</strong>. Link the device at install / after contract when possible.
        </p>
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy || !selected}>
            {busy ? <><IconSpinner size={14} /> Assigning…</> : "Assign Customer"}
          </AppButton>
          <AppButton
            type="button"
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={busy}
          >
            Cancel
          </AppButton>
        </div>
      </form>
    </AppModal>
  );
}

// ─── Suspend modal (requires reason) ─────────────────────────────────────────

function SuspendModal({
  serial,
  open,
  onClose,
  onSuccess,
}: {
  serial: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) { setErr("Reason is required"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/devices/${serial}/suspend`, { reason });
      setReason("");
      onSuccess();
      onClose();
    } catch (ex: any) {
      setErr(ex.message || "Suspend failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Suspend Device">
      <form onSubmit={handleSubmit}>
        <p className="pg-helper-text" style={{ color: "var(--pg-error)" }}>
          <IconWarning size={14} /> Suspending locks the device immediately. The
          customer will lose access until you restore it.
        </p>
        <AppField label="Reason for suspension" error={err}>
          <AppInput
            id="suspend-reason"
            type="text"
            placeholder="e.g. Payment overdue 90 days"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            autoFocus
          />
        </AppField>
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>
            {busy ? <><IconSpinner size={14} /> Suspending…</> : "Confirm Suspend"}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </AppButton>
        </div>
      </form>
    </AppModal>
  );
}

// ─── Unlock token result modal ────────────────────────────────────────────────

function UnlockTokenModal({
  token,
  serial,
  onClose,
}: {
  token: string;
  serial: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <AppModal open={!!token} onClose={onClose} title="Permanent Unlock Token Generated">
      <p className="pg-helper-text">
        <IconCheck size={14} /> Read this token to the customer or send it via SMS.
        It permanently removes all PayGO restrictions from <strong>{serial}</strong>.
      </p>
      <div className="pg-token-display">
        <code id="unlock-token-value">{token}</code>
      </div>
      <div className="pg-modal-actions">
        <AppButton type="button" onClick={copy}>
          {copied ? <><IconCheck size={14} /> Copied!</> : "Copy Token"}
        </AppButton>
        <AppButton type="button" variant="secondary" onClick={onClose}>
          Close
        </AppButton>
      </div>
    </AppModal>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export function DeviceDetail() {
  const { serial } = useParams<{ serial: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPrefill = readEntityPrefill(searchParams);
  const prefillCustomerId = urlPrefill.customer_id
    ? Number(urlPrefill.customer_id) || null
    : null;
  const [device, setDevice] = useState<DeviceData | null>(null);
  const [pageStatus, setPageStatus] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");

  // Action modals
  const [assignOpen, setAssignOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [unlockToken, setUnlockToken] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [autoAssignOpened, setAutoAssignOpened] = useState(false);

  // Inline action states (busy / error per action)
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [confirmDecommission, setConfirmDecommission] = useState(false);

  // ── Data fetching ────────────────────────────────────────────────────────

  const loadDevice = async () => {
    setPageStatus("loading");
    try {
      const data = await api.get<{ device: DeviceData }>(`/devices/${serial}`);
      setDevice(data.device);
      setPageStatus("success");
    } catch (ex: any) {
      setPageError(
        (ex instanceof ApiError && typeof ex.details?.hint === "string"
          ? ex.details.hint
          : null) ||
          ex.message ||
          "Failed to load device",
      );
      setPageStatus("error");
    }
  };

  useEffect(() => { loadDevice(); }, [serial]);

  useEffect(() => {
    if (autoAssignOpened || prefillCustomerId == null || !device || device.status !== "unassigned") return;
    setAssignOpen(true);
    setAutoAssignOpened(true);
  }, [prefillCustomerId, device?.status, autoAssignOpened]);

  // ── Generic lifecycle action ─────────────────────────────────────────────

  const runAction = async (action: string, body: Record<string, unknown> = {}) => {
    if (!device) return;
    setActionBusy(action);
    setActionError("");
    try {
      await api.post(`/devices/${device.serial_number}/${action}`, body);
      await loadDevice();
    } catch (ex: any) {
      setActionError(ex.message || `${action} failed`);
    } finally {
      setActionBusy(null);
    }
  };

  const handlePermanentUnlock = async () => {
    if (!device) return;
    setActionBusy("unlock");
    setActionError("");
    try {
      const res = await api.post<{ token: string }>(
        `/devices/${device.serial_number}/testlab/unlock`,
        {}
      );
      setUnlockToken(res.token);
      await loadDevice();
    } catch (ex: any) {
      setActionError(ex.message || "Unlock failed");
    } finally {
      setActionBusy(null);
    }
  };

  // ── 4 screen states ──────────────────────────────────────────────────────

  if (pageStatus === "loading") {
    return (
      <div className="pg-state">
        <span className="state-icon info"><IconSpinner size={40} /></span>
        <h2>Loading device…</h2>
      </div>
    );
  }

  if (pageStatus === "error" || !device) {
    return (
      <div className="pg-state">
        <span className="state-icon error"><IconError size={40} /></span>
        <h2>Device not found</h2>
        <p>{pageError}</p>
        <p className="pg-text-muted">
          Commercial Officers only see devices for their company. Open Devices from the menu,
          or ask a System Admin if this serial belongs to another company.
        </p>
        <div className="pg-state-actions">
          <AppButton type="button" onClick={loadDevice}>Retry</AppButton>
          <Link to="/devices">
            <AppButton type="button" variant="secondary">← Back to Device Management</AppButton>
          </Link>
        </div>
      </div>
    );
  }

  const { monitoring } = device;
  const s = device.status;

  // Lifecycle state machine — which actions are valid right now
  const canAssign      = s === "unassigned";
  const canActivate    = s === "assigned";
  const canUnassign    = s === "assigned";
  const canSuspend     = s === "active" || s === "assigned";
  const canUnsuspend   = s === "suspended";
  const canUnlock      = device.is_paygo && (s === "active" || s === "suspended");
  const canDecommission = s !== "decommissioned" && s !== "unlocked";

  return (
    <>
      {/* ── Assign Customer modal ── */}
      <AssignCustomerModal
        serial={device.serial_number}
        prefillCustomerId={prefillCustomerId}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSuccess={loadDevice}
      />

      {/* ── Suspend modal ── */}
      <SuspendModal
        serial={device.serial_number}
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        onSuccess={loadDevice}
      />

      {/* ── Unlock token modal ── */}
      {unlockToken && (
        <UnlockTokenModal
          token={unlockToken}
          serial={device.serial_number}
          onClose={() => setUnlockToken("")}
        />
      )}

      {/* ── Breadcrumb ── */}
      <nav className="pg-breadcrumb" aria-label="Breadcrumb">
        <Link to="/devices">Device Management</Link>
        <IconChevronRight size={12} />
        <span>{device.serial_number}</span>
      </nav>

      {/* ── Status bar ── */}
      <AppCard>
        <div className="pg-device-header">
          <div className="pg-device-header__title">
            <h2>{device.serial_number}</h2>
            <p className="pg-text-muted">{device.model ?? "Unknown model"}{device.capacity ? ` · ${device.capacity}` : ""}</p>
          </div>
          <div className="pg-device-header__badges">
            <DeviceStatusBadge status={device.status} />
            <AccessStateBadge state={monitoring.access_state} />
            {monitoring.days_remaining !== null && (
              <AppBadge tone={monitoring.days_remaining < 7 ? "warning" : "info"}>
                <IconInfo size={12} /> {monitoring.days_remaining} day{monitoring.days_remaining !== 1 ? "s" : ""} remaining
              </AppBadge>
            )}
          </div>
        </div>

        {/* ── Action error banner ── */}
        {actionError && (
          <div className="pg-inline-error" role="alert">
            <IconError size={14} /> {actionError}
          </div>
        )}

        {/* ── Lifecycle actions (context-aware) ── */}
        <div className="pg-action-bar">
          {canAssign && (
            <AppButton
              type="button"
              id="btn-assign-customer"
              onClick={() => setAssignOpen(true)}
              disabled={!!actionBusy}
            >
              Assign Customer
            </AppButton>
          )}

          {canActivate && (
            <AppButton
              type="button"
              id="btn-activate"
              onClick={() => runAction("activate")}
              disabled={!!actionBusy}
            >
              {actionBusy === "activate" ? <><IconSpinner size={14} /> Activating…</> : "Activate Device"}
            </AppButton>
          )}

          {canUnassign && (
            <AppButton
              type="button"
              id="btn-unassign"
              variant="secondary"
              onClick={() => runAction("unassign")}
              disabled={!!actionBusy}
            >
              {actionBusy === "unassign" ? <><IconSpinner size={14} /> Unassigning…</> : "Unassign"}
            </AppButton>
          )}

          {canSuspend && (
            <AppButton
              type="button"
              id="btn-suspend"
              variant="secondary"
              onClick={() => setSuspendOpen(true)}
              disabled={!!actionBusy}
            >
              <IconWarning size={14} /> Suspend Device
            </AppButton>
          )}

          {canUnsuspend && (
            <AppButton
              type="button"
              id="btn-unsuspend"
              onClick={() => runAction("unsuspend")}
              disabled={!!actionBusy}
            >
              {actionBusy === "unsuspend" ? <><IconSpinner size={14} /> Restoring…</> : <><IconCheck size={14} /> Restore Access</>}
            </AppButton>
          )}

          {canUnlock && (
            <AppButton
              type="button"
              id="btn-permanent-unlock"
              variant="secondary"
              onClick={() => setConfirmUnlock(true)}
              disabled={!!actionBusy}
            >
              {actionBusy === "unlock" ? <><IconSpinner size={14} /> Generating token…</> : "Test-lab permanent unlock"}
            </AppButton>
          )}

          {canDecommission && (
            <AppButton
              type="button"
              id="btn-decommission"
              variant="secondary"
              onClick={() => setConfirmDecommission(true)}
              disabled={!!actionBusy}
            >
              {actionBusy === "decommission" ? <><IconSpinner size={14} /> Decommissioning…</> : "Decommission"}
            </AppButton>
          )}
        </div>
      </AppCard>

      {/* ── Monitoring block ── */}
      <AppCard title="PayGO Monitoring">
        <AppTable
          head={["Metric", "Value"]}
          rows={[
            ["Access State",      <AccessStateBadge state={monitoring.access_state} />],
            ["Effective Locked",  monitoring.effective_locked
              ? <AppBadge tone="error"><IconError size={12} /> Yes</AppBadge>
              : <AppBadge tone="success"><IconCheck size={12} /> No</AppBadge>
            ],
            ["Paid Through",      monitoring.paid_through
              ? new Date(monitoring.paid_through).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
              : "—"
            ],
            ["Days Remaining",    monitoring.days_remaining !== null ? `${monitoring.days_remaining} days` : "—"],
            ["Last Token (masked)", monitoring.last_token_masked ?? "—"],
            ["Token Counter (server)", <strong>{device.token_count}</strong>],
          ]}
        />
        <p className="pg-text-muted" style={{ marginTop: 8 }}>
          The server counter must match the hardware counter before a token is accepted.
          Import Count from the supplier CSV; only enter tokens that were successfully accepted on the device.
        </p>
      </AppCard>

      {/* ── OpenPAYGO credentials + guided wizard ── */}
      {device.is_paygo && (
        <AppCard title="OpenPAYGO — tokens & counters">
          <AppTable
            head={["Property", "Value"]}
            rows={[
              ["Starting code", device.starting_code?.toString() ?? "—"],
              ["Secret key (masked)", device.secret_key_masked ?? "—"],
              ["Value divider (Time Divider)", String(device.value_divider ?? 1)],
              ["Restricted digit set", device.restricted_digit_set ? "Yes" : "No"],
              ["Credentials ready", device.has_openpaygo_credentials
                ? <AppBadge tone="success"><IconCheck size={12} /> Yes</AppBadge>
                : <AppBadge tone="error"><IconError size={12} /> Missing</AppBadge>
              ],
              ["Server counter", <strong>{device.token_count}</strong>],
            ]}
          />

          <p className="pg-text-muted" style={{ marginTop: 12 }}>
            Guided path: record or approve cash → token. Day credit never
            issues without a payment. First unlock after install uses <strong>Key Approvals</strong>.
            Counter sync / set count / full reset use <strong>OpenPAYGO Exceptions</strong> (dual control).
          </p>

          {device.has_openpaygo_credentials ? (
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
              <AppButton
                type="button"
                onClick={() => setWizardOpen(true)}
              >
                Release PAYG days…
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                onClick={() => navigate(`/openpaygo-exceptions?serial=${encodeURIComponent(device.serial_number)}`)}
              >
                Request OpenPAYGO exception…
              </AppButton>
            </div>
          ) : (
            <p className="pg-helper-text" role="status">
              OpenPAYGO credentials are missing — import secret key / starting code before minting tokens.
            </p>
          )}
        </AppCard>
      )}

      <TokenDeviceWizard
        open={wizardOpen}
        deviceSerial={device.serial_number}
        initialIntent="release_days"
        onClose={() => setWizardOpen(false)}
        onDone={loadDevice}
      />

      {/* ── Device information ── */}
      <AppCard title="Device Information">
        <AppTable
          head={["Property", "Value"]}
          rows={[
            ["Serial Number",   device.serial_number],
            ["Model",           device.model ?? "—"],
            ["Capacity",        device.capacity ?? "—"],
            ["Type",            device.device_type_name ?? "—"],
            ["Manufacturer",    device.manufacturer_name ?? "—"],
            ["PayGO Device",    device.is_paygo
              ? <AppBadge tone="info"><IconCheck size={12} /> Yes</AppBadge>
              : <AppBadge tone="neutral">No</AppBadge>
            ],
            ["Token Counter",   String(device.token_count)],
            ["Starting Code",   device.starting_code?.toString() ?? "—"],
            ["Registered",      new Date(device.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })],
          ]}
        />
      </AppCard>

      {/* ── Customer assignment ── */}
      <AppCard title="Customer Assignment">
        {device.customer ? (
          <>
            <AppTable
              head={["Field", "Value"]}
              rows={[
                ["Customer #",    device.customer.customer_number],
                ["Name",          device.customer.name],
                ["Phone",         device.customer.phone],
                ["Assigned On",   device.assigned_at
                  ? new Date(device.assigned_at).toLocaleDateString("en-GB")
                  : "—"
                ],
                ["Activated On",  device.activated_at
                  ? new Date(device.activated_at).toLocaleDateString("en-GB")
                  : "Not yet activated"
                ],
                ["Unlocked On",   device.unlocked_at
                  ? new Date(device.unlocked_at).toLocaleDateString("en-GB")
                  : "Not permanently unlocked"
                ],
              ]}
            />
            <div className="pg-action-bar" style={{ marginTop: 12 }}>
              <Link to={withEntityQuery("/field/payments/new", { customer_id: device.customer.id })}>
                <AppButton type="button" variant="secondary">Record Payment</AppButton>
              </Link>
              <Link to={withEntityQuery("/installations/new", { customer_id: device.customer.id })}>
                <AppButton type="button" variant="secondary">Schedule Install</AppButton>
              </Link>
              <Link to={withEntityQuery("/tickets", {
                customer_id: device.customer.id,
                device_serial: device.serial_number,
              })}>
                <AppButton type="button" variant="secondary">New Ticket</AppButton>
              </Link>
              <Link to={`/customers/${device.customer.id}`}>
                <AppButton type="button" variant="secondary">Customer Profile</AppButton>
              </Link>
            </div>
          </>
        ) : (
          <div className="pg-state pg-state--inline">
            <span className="state-icon neutral"><IconInfo size={28} /></span>
            <p>No customer assigned. Use <strong>Assign Customer</strong> above to link this device.</p>
          </div>
        )}
      </AppCard>

      <ConfirmAction
        open={confirmUnlock}
        title="Test-lab permanent unlock?"
        message="Generates an unlock token without a loan-completion payment. Prefer DISABLE_PAYG under OpenPAYGO with a completed loan-completion payment for production."
        confirmLabel="Continue unlock"
        danger
        busy={actionBusy === "unlock"}
        onCancel={() => setConfirmUnlock(false)}
        onConfirm={() => {
          setConfirmUnlock(false);
          handlePermanentUnlock();
        }}
      />
      <ConfirmAction
        open={confirmDecommission}
        title="Decommission device?"
        message={`Permanently decommission ${device.serial_number}? This cannot be undone.`}
        confirmLabel="Decommission"
        confirmPhrase={device.serial_number}
        danger
        busy={actionBusy === "decommission"}
        onCancel={() => setConfirmDecommission(false)}
        onConfirm={() => {
          setConfirmDecommission(false);
          runAction("decommission", { reason: "Manual decommission via admin console" });
        }}
      />
    </>
  );
}
